// src/pages/FiveStackPlanner.jsx
import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, Sparkles, Target, Users, RotateCcw, Wand2 } from "lucide-react";

const apiUrl = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001';

/* ---------- Aurora theme ---------- */
const AURORA_BG = {
    backgroundColor: "#0b1220",
    backgroundImage: `
    radial-gradient(1200px 800px at -20% -10%, rgba(14,165,233,.25), transparent 60%),
    radial-gradient(1200px 800px at 120% 0%, rgba(34,211,238,.22), transparent 55%),
    radial-gradient(1000px 800px at 110% 120%, rgba(168,85,247,.18), transparent 55%),
    radial-gradient(800px 600px at 0% 120%, rgba(16,185,129,.16), transparent 55%)
  `,
};
const CARD = "rounded-2xl border border-white/10 bg-white/[.05] backdrop-blur-md shadow-[0_10px_30px_rgba(0,0,0,.25)]";

const norm = (s) => (s ?? "").toString().toLowerCase();
const LANES = ["top","jg","mid","adc","sup"];
const LANE_LABEL = { top:"Top", jg:"Jungle", mid:"Mid", adc:"ADC", sup:"Support" };

/* Limit used only for enumerating “best comp” search (UI lists still show ALL) */
const ENUM_LIMIT_PER_LANE = 8;

/* =========================================================
   1) Canonical 5-stack challenges (your dataset, verbatim)
   ========================================================= */
const CHALLENGE_CANON = {
    "Summoners on the Rift": [
        "Annie","Azir","Bel'Veth","Elise","Fiddlesticks","Heimerdinger","Illaoi","Ivern","Kalista","Kindred","Lissandra","Malzahar","Maokai","Naafiri","Neeko","Orianna","Shaco","Yorick","Yunara","Zed","Zyra"
    ],
    "Nowhere to Hide": [
        "Akshan","Ashe","Aurelion Sol","Bard","Briar","Caitlyn","Draven","Ezreal","Galio","Gangplank","Jhin","Jinx","Kai'Sa","Karthus","Kled","Lillia","Lux","Maokai","Mel","Nocturne","Pantheon","Ryze","Taliyah","Twisted Fate","Vex","Xerath","Yunara","Ziggs"
    ],
    "It Has 'Ultimate' in the Name": [
        "Amumu","Anivia","Annie","Aurelion Sol","Aurora","Azir","Bard","Bel'Veth","Blitzcrank","Braum","Briar","Cassiopeia","Corki","Diana","Draven","Ekko","Evelynn","Ezreal","Fiddlesticks","Fizz","Galio","Gangplank","Gnar","Gragas","Gwen","Heimerdinger","Illaoi","Janna","Jarvan IV","Jinx","Karthus","Katarina","Kayle","Kennen","Leona","Lissandra","Lux","Malphite","Maokai","Mel","Milio","Miss Fortune","Morgana","Nami","Neeko","Nilah","Nunu & Willump","Renata Glasc","Riven","Rumble","Samira","Sejuani","Senna","Seraphine","Sett","Skarner","Smolder","Sona","Swain","Talon","Twitch","Vel'Koz","Viktor","Vladimir","Volibear","Xayah","Yone","Yuumi","Zac","Zeri","Ziggs","Zyra"
    ],
    "We Protec": [
        "Alistar","Annie","Bard","Briar","Fiora","Galio","Hwei","Ivern","Janna","K'Sante","Karma","Kayle","Kindred","Lee Sin","Lulu","Lux","Milio","Morgana","Naafiri","Nami","Nidalee","Skarner","Sona","Soraka","Tahm Kench","Taric","Thresh","Yuumi","Zilean"
    ],
    "They Just... Don't... Die!": [
        "Akshan","Alistar","Anivia","Bard","Bel'Veth","Braum","Ekko","Elise","Evelynn","Fiora","Fizz","Gwen","Jax","K'Sante","Kalista","Karthus","Kayle","Kayn","Kindred","Kled","Kog'Maw","LeBlanc","Maokai","Master Yi","Renata Glasc","Sion","Tahm Kench","Taric","Tryndamere","Vladimir","Yuumi","Zac","Zed","Zilean"
    ],
    "Where’d They Go?": [
        "Akali","Akshan","Evelynn","Kai'Sa","Kha'Zix","LeBlanc","Neeko","Nocturne","Pyke","Qiyana","Rengar","Senna","Shaco","Talon","Teemo","Twitch","Vayne","Viego","Wukong"
    ],
    "We're Good Over Here": [
        "Akshan","Ashe","Aurora","Azir","Caitlyn","Cho'Gath","Corki","Dr. Mundo","Ezreal","Gangplank","Hwei","Jayce","Jhin","Jinx","Kai'Sa","Karma","Kled","Kog'Maw","Lux","Maokai","Mel","Smolder","Taliyah","Twisted Fate","Varus","Vel'Koz","Vex","Viktor","Xayah","Xerath","Yuumi","Zeri","Ziggs","Zoe"
    ],
    "Get Over Here": [
        "Alistar","Azir","Blitzcrank","Darius","Gnar","Gragas","Hecarim","Janna","Jayce","K'Sante","Kled","Lee Sin","Maokai","Mordekaiser","Nautilus","Nilah","Orianna","Poppy","Pyke","Rell","Sett","Singed","Skarner","Swain","Tahm Kench","Taliyah","Thresh","Tristana","Urgot","Vayne","Yone","Zac","Ziggs"
    ],
    "It's a Trap!": [
        "Caitlyn","Gangplank","Jhin","Jinx","Maokai","Nidalee","Shaco","Ziggs","Zyra"
    ],
    "I'm Helping": [
        "Anivia","Aurora","Azir","Irelia","Ivern","Jarvan IV","Ornn","Taliyah","Trundle","Veigar","Yorick"
    ],
    "Hold That Pose": [
        "Aatrox","Alistar","Amumu","Anivia","Bard","Blitzcrank","Braum","Briar","Camille","Galio","Gnar","Gragas","Hecarim","Ivern","Janna","Jarvan IV","K'Sante","Kled","LeBlanc","Leona","Lissandra","Lulu","Maokai","Morgana","Nami","Neeko","Nilah","Ornn","Poppy","Pyke","Qiyana","Rakan","Rammus","Rell","Renata Glasc","Riven","Sejuani","Seraphine","Sett","Shaco","Singed","Sion","Skarner","Tahm Kench","Thresh","Urgot","Vi","Warwick","Yasuo","Yone","Zac","Zyra"
    ],
    "5 Under 5'": [
        "Corki","Fizz","Gnar","Heimerdinger","Kennen","Kled","Lulu","Poppy","Rumble","Teemo","Tristana","Veigar","Vex","Yuumi","Ziggs"
    ],
    "All Hands on Deck": [
        "Fizz","Gangplank","Graves","Illaoi","Miss Fortune","Nautilus","Nilah","Pyke","Tahm Kench","Twisted Fate"
    ],
    "For Demacia": [
        "Fiora","Galio","Garen","Jarvan IV","Kayle","Lucian","Lux","Morgana","Poppy","Quinn","Shyvana","Sona","Sylas","Vayne"
    ],
    "Ice, Ice, Baby": [
        "Anivia","Ashe","Aurora","Braum","Gnar","Gragas","Lissandra","Nunu & Willump","Olaf","Ornn","Sejuani","Trundle","Tryndamere","Udyr","Volibear"
    ],
    "Everybody Was Wuju Fighting": [
        "Ahri","Akali","Hwei","Irelia","Ivern","Jhin","Karma","Kayn","Kennen","Lee Sin","Lillia","Master Yi","Rakan","Sett","Shen","Syndra","Varus","Wukong","Xayah","Yasuo","Yone","Yunara","Zed"
    ],
    "Elemental, My Dear Watson": [
        "Malphite","Milio","Neeko","Nidalee","Qiyana","Rengar","Skarner","Zyra"
    ],
    "Strength Above All": [
        "Ambessa","Briar","Cassiopeia","Darius","Draven","Katarina","Kled","LeBlanc","Mel","Mordekaiser","Rell","Riven","Samira","Sion","Swain","Talon","Vladimir"
    ],
    "Calculated": [
        "Caitlyn","Camille","Corki","Ezreal","Heimerdinger","Jayce","Orianna","Seraphine","Vi"
    ],
    "Spooky Scary Skeletons": [
        "Elise","Fiddlesticks","Gwen","Hecarim","Kalista","Karthus","Maokai","Senna","Thresh","Vex","Viego","Yorick"
    ],
    "The Sun Disc Never Sets": [
        "Akshan","Amumu","Azir","K'Sante","Naafiri","Nasus","Rammus","Renekton","Sivir","Taliyah","Xerath"
    ],
    "Peak Performance": [
        "Aphelios","Aurelion Sol","Diana","Leona","Pantheon","Soraka","Taric","Zoe"
    ],
    "(Inhuman Screeching Sounds)": [
        "Bel'Veth","Cho'Gath","Kai'Sa","Kassadin","Kha'Zix","Kog'Maw","Malzahar","Rek'Sai","Vel'Koz"
    ],
    "Chemtech Comrades": [
        "Blitzcrank","Dr. Mundo","Ekko","Janna","Jinx","Renata Glasc","Singed","Twitch","Urgot","Viktor","Warwick","Zac","Zeri","Ziggs"
    ]
};

/* =========================================================
   2) Rules per challenge (no tooltip parsing; just team logic)
   - type: 'count' (need >= min from list) or 'all5' (all picks from list)
   ========================================================= */
const FEATURE_3 = new Set([
    "Nowhere to Hide",
    "It Has 'Ultimate' in the Name",
    "We Protec",
    "They Just... Don't... Die!",
    "Where’d They Go?",
    "We're Good Over Here",
    "Get Over Here",
    "It's a Trap!",
    "I'm Helping",
    "Hold That Pose"
]);

const ALL5 = new Set([
    "Summoners on the Rift",
    "5 Under 5'",
    "All Hands on Deck",
    "For Demacia",
    "Ice, Ice, Baby",
    "Everybody Was Wuju Fighting",
    "Elemental, My Dear Watson",
    "Strength Above All",
    "Calculated",
    "Spooky Scary Skeletons",
    "The Sun Disc Never Sets",
    "Peak Performance",
    "(Inhuman Screeching Sounds)",
    "Chemtech Comrades"
]);

const CHALLENGE_RULES = Object.fromEntries(
    Object.keys(CHALLENGE_CANON).map(name => {
        if (FEATURE_3.has(name)) return [name, { type: "count", min: 3 }];
        if (ALL5.has(name))      return [name, { type: "all5", min: 5 }];
        // Safe default
        return [name, { type: "count", min: 3 }];
    })
);

/* Optional short descriptions for UI */
const CHALLENGE_DESCRIPTIONS = {
    "Summoners on the Rift": "As a premade 5, win with all 5 playing summon/pet champs.",
    "Nowhere to Hide": "As a premade 5, win with ≥3 champs with global/long-range impact.",
    "It Has 'Ultimate' in the Name": "As a premade 5, win with ≥3 champs whose ult excels in teamfights.",
    "We Protec": "As a premade 5, win with ≥3 healers/shielders.",
    "They Just... Don't... Die!": "As a premade 5, win with ≥3 champs with revive/invuln/zombie effects.",
    "Where’d They Go?": "As a premade 5, win with ≥3 stealth/camouflage champs.",
    "We're Good Over Here": "As a premade 5, win with ≥3 strong poke champs.",
    "Get Over Here": "As a premade 5, win with ≥3 champs with displacement.",
    "It's a Trap!": "As a premade 5, win with ≥3 trap-champs.",
    "I'm Helping": "As a premade 5, win with ≥3 terrain-creating champs.",
    "Hold That Pose": "As a premade 5, win with ≥3 champs with 2+ immobilizes.",
    "5 Under 5'": "As a premade 5, win with 5 shorties.",
    "All Hands on Deck": "As a premade 5, win with 5 Bilgewater crew.",
    "For Demacia": "As a premade 5, win with 5 Demacians.",
    "Ice, Ice, Baby": "As a premade 5, win with 5 Freljord champs.",
    "Everybody Was Wuju Fighting": "As a premade 5, win with 5 Ionian champs.",
    "Elemental, My Dear Watson": "As a premade 5, win with 5 element-themed champs.",
    "Strength Above All": "As a premade 5, win with 5 Noxians.",
    "Calculated": "As a premade 5, win with 5 Piltover techies.",
    "Spooky Scary Skeletons": "As a premade 5, win with 5 Shadow Isles/Camavor spooky folks.",
    "The Sun Disc Never Sets": "As a premade 5, win with 5 Shurimans.",
    "Peak Performance": "As a premade 5, win with 5 Targon champs.",
    "(Inhuman Screeching Sounds)": "As a premade 5, win with 5 Void champs.",
    "Chemtech Comrades": "As a premade 5, win with 5 Zaun/Chemtech champs."
};

/* Reason templates (per-challenge; keeps it simple & consistent) */
const REASON_TEMPLATE = {
    "Summoners on the Rift": "has a summon/pet/clone/minion mechanic.",
    "Nowhere to Hide": "has global or map-spanning reach/impact.",
    "It Has 'Ultimate' in the Name": "brings a high-impact, teamfight-winning ultimate.",
    "We Protec": "brings ally healing/shielding or strong protection.",
    "They Just... Don't... Die!": "provides revive/invulnerability/zombie or equivalent survival effect.",
    "Where’d They Go?": "has stealth or camouflage windows.",
    "We're Good Over Here": "provides reliable long-range poke.",
    "Get Over Here": "has a displacement (pull/push/knockback/drag/hook).",
    "It's a Trap!": "places traps/mines/boxes/chompers/saplings.",
    "I'm Helping": "creates terrain (wall/pillar/cage/brush/arena).",
    "Hold That Pose": "kit includes ≥2 distinct immobilizing spells.",
    "5 Under 5'": "is under five feet tall (tiny terror).",
    "All Hands on Deck": "is a Bilgewater champion.",
    "For Demacia": "is a Demacian.",
    "Ice, Ice, Baby": "is a Freljord champion.",
    "Everybody Was Wuju Fighting": "is an Ionian.",
    "Elemental, My Dear Watson": "fits the element-themed roster.",
    "Strength Above All": "is a Noxian.",
    "Calculated": "is a Piltover/tech champion.",
    "Spooky Scary Skeletons": "fits the Shadow Isles/Camavor spooky roster.",
    "The Sun Disc Never Sets": "is a Shuriman.",
    "Peak Performance": "is a Targon champion.",
    "(Inhuman Screeching Sounds)": "is a champion of the Void.",
    "Chemtech Comrades": "is from Zaun/Chemtech lineup."
};

/* =========================================================
   3) Data Dragon (only for icons + light role scoring)
   ========================================================= */
function useDDChampionFull() {
    const [ver, setVer] = useState(null);
    const [champs, setChamps] = useState([]);
    const [idByName, setIdByName] = useState(new Map());
    useEffect(() => {
        let ok = true;
        (async () => {
            try {
                const vs = await fetch("https://ddragon.leagueoflegends.com/api/versions.json").then(r=>r.json());
                const v = vs?.[0]; if (!v || !ok) return;
                setVer(v);
                const full = await fetch(`https://ddragon.leagueoflegends.com/cdn/${v}/data/en_US/championFull.json`).then(r=>r.json());
                const arr = Object.values(full?.data || {}).map(d => ({
                    id: d.id,
                    name: d.name,
                    tags: (d.tags || []).map(t => t.toLowerCase()),
                    partype: (d.partype || "mana").toLowerCase(),
                    range: Number(d.stats?.attackrange ?? 125)
                }));
                if (!ok) return;
                setChamps(arr);
                setIdByName(new Map(arr.map(d => [d.name, d.id])));
            } catch {/* ignore */}
        })();
        return () => { ok = false; };
    }, []);
    const iconById = (cid) => ver ? `https://ddragon.leagueoflegends.com/cdn/${ver}/img/champion/${cid}.png` : null;
    const iconByName = (name) => {
        const cid = idByName.get(name);
        return cid ? iconById(cid) : null;
    };
    const byName = new Map(champs.map(c => [c.name, c]));
    return { iconByName, champByName: byName };
}

/* Role scoring (simple heuristic off tags/range) */
function scoreRolesFromDD(ch) {
    if (!ch) return ["mid","sup","jg","top","adc"]; // soft fallback
    const tags = ch.tags || [];
    const range = ch.range ?? 125;
    const pt = ch.partype || "mana";
    const s = { top:0, jg:0, mid:0, adc:0, sup:0 };

    if (tags.includes("marksman")) { s.adc += 3; s.mid += 0.4; s.sup += 0.2; s.top -= 0.5; s.jg -= 0.6; }
    if (tags.includes("mage"))     { s.mid += 2; s.sup += 0.8; }
    if (tags.includes("assassin")) { s.mid += 1.8; s.jg += 1.2; s.top += 0.3; }
    if (tags.includes("fighter"))  { s.top += 1.7; s.jg += 1.1; s.mid += 0.3; }
    if (tags.includes("tank"))     { s.top += 1.9; s.sup += 1.1; s.jg += 0.7; }
    if (tags.includes("support"))  { s.sup += 3; s.mid += 0.2; }
    if (range >= 525) { s.adc += 1.1; s.mid += 0.5; s.sup += 0.4; s.top -= 0.4; s.jg -= 0.7; }
    else if (range <= 175) { s.top += 0.5; s.jg += 0.4; s.adc -= 0.8; }
    if (pt === "energy") { s.mid += 0.3; s.top += 0.2; }
    return Object.entries(s).sort((a,b)=>b[1]-a[1]).map(([lane])=>lane);
}

/* =========================================================
   4) Challenge helpers (purely from canonical data)
   ========================================================= */
const CHALLENGE_LIST = Object.keys(CHALLENGE_CANON);

/* Does a 5-stack meet a challenge? */
function teamMeetsChallenge(teamNames, challengeName) {
    const rule = CHALLENGE_RULES[challengeName] || { type:"count", min:3 };
    const eligible = new Set(CHALLENGE_CANON[challengeName] || []);
    if (rule.type === "all5") {
        return teamNames.length === 5 && teamNames.every(n => eligible.has(n));
    } else { // 'count'
        let cnt = 0;
        teamNames.forEach(n => { if (eligible.has(n)) cnt++; });
        return cnt >= (rule.min || 3);
    }
}

/* How many/which challenges a team covers (across ALL) */
function challengesCoveredByTeam(teamNames) {
    const covered = [];
    for (const chName of CHALLENGE_LIST) {
        if (teamMeetsChallenge(teamNames, chName)) covered.push(chName);
    }
    return covered;
}

/* For a given challenge -> per-lane lists (ALL options) */
function buildLaneListsForChallenge(challengeName, champByName) {
    const eligible = CHALLENGE_CANON[challengeName] || [];
    // Precompute champ “individual coverage” (how many total challenges this champ appears in)
    const coverageCount = new Map();
    for (const name of eligible) {
        let cnt = 0;
        for (const k of CHALLENGE_LIST) if ((CHALLENGE_CANON[k] || []).includes(name)) cnt++;
        coverageCount.set(name, cnt);
    }

    // Fill lane buckets
    const lists = Object.fromEntries(LANES.map(l => [l, []]));
    eligible.forEach(name => {
        const ch = champByName.get(name);
        const roles = scoreRolesFromDD(ch).slice(0, 2);
        roles.forEach((lane, i) => {
            lists[lane].push({
                name,
                laneScore: (i === 0 ? 5 : 3),
                coverageCount: coverageCount.get(name) || 1,
            });
        });
    });

    // Sort each lane: more global coverage first, then laneScore, then alpha
    LANES.forEach(lane => {
        lists[lane].sort((a,b) =>
            (b.coverageCount - a.coverageCount) ||
            (b.laneScore - a.laneScore) ||
            a.name.localeCompare(b.name)
        );
    });

    return lists;
}

/* Choose the “best comp” for a selected challenge
   - Enumerate using TOP N per lane (ENUM_LIMIT_PER_LANE) to keep it fast
   - Objective: maximize number of total challenges covered by that 5-stack
*/
function bestTeamForChallenge(laneLists) {
    const arrays = LANES.map(l => (laneLists[l] || []).slice(0, ENUM_LIMIT_PER_LANE));
    if (arrays.some(a => a.length === 0)) return { teamMap:{}, covered:[] };

    let best = { teamMap:{}, covered:[] };
    let bestKey = [-1, -1]; // [coveredCount, tieBreaker]

    const used = new Set();
    const pick = {};

    const recur = (i) => {
        if (i === LANES.length) {
            const teamNames = LANES.map(l => pick[l].name);
            const covered = challengesCoveredByTeam(teamNames);
            // Tie-breaker: sum of laneScore (+ small reward for unique names is implicit)
            const laneBonus = LANES.reduce((s,l)=> s + (pick[l].laneScore||0), 0);
            const key = [covered.length, laneBonus];
            if (key[0] > bestKey[0] || (key[0] === bestKey[0] && key[1] > bestKey[1])) {
                bestKey = key;
                best = { teamMap:Object.fromEntries(LANES.map(l=>[l, pick[l].name])), covered };
            }
            return;
        }
        const lane = LANES[i];
        for (const cand of arrays[i]) {
            if (used.has(cand.name)) continue;
            used.add(cand.name);
            pick[lane] = cand;
            recur(i+1);
            used.delete(cand.name);
        }
    };

    recur(0);
    return best;
}

/* Notes + reasons */
function notesForChallenge(name) {
    const rule = CHALLENGE_RULES[name] || { type:"count", min:3 };
    const base = [];
    if (rule.type === "all5") base.push("All 5 champions must be from the listed roster.");
    else base.push(`At least ${rule.min} of your 5 champions must be from the listed roster.`);
    if (CHALLENGE_DESCRIPTIONS[name]) base.push(CHALLENGE_DESCRIPTIONS[name]);
    return base;
}

function eligibleWithReasons(name) {
    const roster = CHALLENGE_CANON[name] || [];
    const reason = REASON_TEMPLATE[name] || "is eligible for this challenge.";
    return roster.map(ch => ({ name: ch, reason }));
}

/* =========================================================
   5) UI
   ========================================================= */
export default function FiveStackPlanner() {
    const { iconByName, champByName } = useDDChampionFull();

    const [q, setQ] = useState("");
    const [selectedName, setSelectedName] = useState(CHALLENGE_LIST[0]);

    const laneLists = useMemo(() => buildLaneListsForChallenge(selectedName, champByName), [selectedName, champByName]);

    const bestAuto = useMemo(() => bestTeamForChallenge(laneLists), [laneLists]);

    const [teamMap, setTeamMap] = useState({});
    const [activeChallenge, setActiveChallenge] = useState(selectedName);

    useEffect(() => {
        if (activeChallenge !== selectedName) {
            // Always preselect the best comp for this challenge
            setTeamMap(bestAuto.teamMap || {});
            setActiveChallenge(selectedName);
        }
    }, [selectedName, bestAuto.teamMap, activeChallenge]);

    const takenByLane = (name) => LANES.find(l => teamMap[l] === name) || null;
    const handlePick  = (lane, name) => {
        const taken = takenByLane(name);
        if (taken && taken !== lane) return; // enforce uniqueness
        setTeamMap(prev => ({ ...prev, [lane]: name }));
    };
    const handleClear = () => setTeamMap({});
    const handleAuto  = () => setTeamMap(bestAuto.teamMap || {});

    const teamNames = useMemo(() => LANES.map(l => teamMap[l]).filter(Boolean), [teamMap]);
    const coveredNow = useMemo(() => (teamNames.length === 5) ? challengesCoveredByTeam(teamNames) : [], [teamNames]);

    const filteredChallenges = useMemo(() => {
        const k = norm(q);
        return CHALLENGE_LIST.filter(n => !k || n.toLowerCase().includes(k));
    }, [q]);

    const notes = useMemo(() => notesForChallenge(selectedName), [selectedName]);
    const eligibleList = useMemo(() => eligibleWithReasons(selectedName), [selectedName]);

    return (
        <div className="min-h-screen text-slate-200" style={AURORA_BG}>
            <header className="sticky top-0 z-30 border-b border-white/10 backdrop-blur-md" style={{ background: "linear-gradient(to right, rgba(15,23,42,.65), rgba(15,23,42,.45))" }}>
                <div className="max-w-6xl mx-auto px-3 sm:px-4 py-3 flex items-center gap-3">
                    <div className="text-lg sm:text-xl font-extrabold tracking-tight">5-Stack Challenge Helper <span className="opacity-60">— Aurora</span></div>
                    <div className="flex-1" />
                    <div className="relative w-72">
                        <input
                            value={q}
                            onChange={(e)=>setQ(e.target.value)}
                            placeholder="Search 5-stack challenges…"
                            className="w-full bg-white/5 text-slate-100 border border-white/10 rounded-xl px-4 py-2 pr-9 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-cyan-400/30"
                        />
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 rotate-90 text-slate-400 pointer-events-none" />
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-3 sm:px-4 py-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left: challenge list */}
                <section className={`${CARD} p-3 lg:col-span-1`}>
                    <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">5-Stack Challenges</div>
                    <div className="max-h-[70vh] overflow-auto space-y-2 pr-1">
                        {filteredChallenges.map((name) => {
                            const rule = CHALLENGE_RULES[name];
                            const tags = [];
                            tags.push(rule.type === "all5" ? "All 5 from list" : `≥${rule.min} from list`);
                            const active = selectedName === name;
                            return (
                                <button
                                    key={name}
                                    onClick={() => setSelectedName(name)}
                                    className={`w-full text-left p-3 rounded-xl border transition ${active ? "border-cyan-400/30 bg-cyan-400/10" : "border-white/10 bg-white/5 hover:bg-white/10"}`}
                                >
                                    <div className="text-sm font-semibold truncate">{name}</div>
                                    <div className="text-xs text-slate-400 line-clamp-2">{CHALLENGE_DESCRIPTIONS[name] || "—"}</div>
                                    <div className="text-xs text-slate-500 mt-1">{tags.join(" • ")}</div>
                                </button>
                            );
                        })}
                    </div>
                </section>

                {/* Right: detail */}
                <section className="lg:col-span-2 space-y-6">
                    {/* Challenge header + comp */}
                    <div className={`${CARD} p-4`}>
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <div className="text-sm font-bold text-slate-100">{selectedName}</div>
                                <div className="text-slate-300 text-sm mt-1">{CHALLENGE_DESCRIPTIONS[selectedName] || "—"}</div>
                                <div className="text-xs text-slate-400 mt-2">
                                    {CHALLENGE_RULES[selectedName]?.type === "all5"
                                        ? <>Requirement: <b>All 5</b> champions from the listed roster.</>
                                        : <>Requirement: <b>≥{CHALLENGE_RULES[selectedName]?.min || 3}</b> champions from the listed roster.</>}
                                </div>
                            </div>
                        </div>

                        {/* Interactive team */}
                        <div className="mt-4">
                            <div className="flex items-center gap-2 mb-2">
                                <Users className="w-4 h-4 text-emerald-300" />
                                <h3 className="text-sm font-bold text-slate-100">Your team (click options below to change)</h3>
                                <div className="text-xs text-slate-400 ml-auto flex items-center gap-2">
                                    <span>Covers: <b>{coveredNow.length}</b> challenge{coveredNow.length === 1 ? "" : "s"}</span>
                                    {/* Mark if equals our precomputed best */}
                                    {LANES.every(l => bestAuto.teamMap[l] === teamMap[l]) && (
                                        <span className="px-1 py-0.5 rounded bg-emerald-400/10 border border-emerald-400/30 text-emerald-200">best</span>
                                    )}
                                </div>
                            </div>

                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                                {LANES.map(lane => {
                                    const name = teamMap[lane];
                                    return (
                                        <div key={lane} className={`px-3 py-2 rounded-xl border ${name ? "border-white/10 bg-white/5" : "border-dashed border-white/10 bg-white/5/0"}`}>
                                            <div className="flex items-center gap-2">
                                                {name ? (
                                                    <>
                                                        {iconByName(name) && <img src={iconByName(name)} alt={name} className="w-6 h-6 rounded-md" />}
                                                        <div className="text-sm font-medium">{name}</div>
                                                    </>
                                                ) : (
                                                    <div className="text-sm text-slate-400">Pick a champion</div>
                                                )}
                                                <div className="ml-auto text-[11px] text-slate-400">{LANE_LABEL[lane]}</div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="mt-2 flex gap-2">
                                <button onClick={handleAuto} className="px-2.5 py-1.5 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-xs flex items-center gap-1">
                                    <Wand2 className="w-4 h-4" /> Auto-pick best
                                </button>
                                <button onClick={handleClear} className="px-2.5 py-1.5 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-xs flex items-center gap-1">
                                    <RotateCcw className="w-4 h-4" /> Clear team
                                </button>
                            </div>

                            {/* Covered challenges by this comp */}
                            <div className="mt-3 grid grid-cols-1 gap-2 text-xs">
                                <div className="p-2 rounded-xl border border-white/10 bg-white/5 max-h-48 overflow-auto">
                                    <div className="uppercase tracking-wide text-slate-400 mb-1">Challenges covered by this team ({coveredNow.length}):</div>
                                    <ul className="list-disc pl-5">
                                        {coveredNow.map(n => <li key={n}>{n}</li>)}
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* Per-role panels: show ALL options */}
                        <div className="mt-6">
                            <div className="flex items-center gap-2 mb-2">
                                <Target className="w-4 h-4 text-cyan-300" />
                                <h3 className="text-sm font-bold text-slate-100">All options per role</h3>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {LANES.map((lane) => {
                                    const list = laneLists[lane] || [];
                                    const selectedNameInLane = teamMap[lane];
                                    const sorted = list.slice(); // already sorted

                                    return (
                                        <div key={lane} className="p-3 rounded-xl border border-white/10 bg-white/5">
                                            <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">{LANE_LABEL[lane]}</div>
                                            <div className="grid grid-cols-1 gap-2 max-h-64 overflow-auto pr-1">
                                                {sorted.map((cand) => {
                                                    const isSelected = selectedNameInLane === cand.name;
                                                    const takenElsewhere = (() => {
                                                        const taken = LANES.find(l => teamMap[l] === cand.name);
                                                        return taken && taken !== lane ? taken : null;
                                                    })();
                                                    const disabled = !!takenElsewhere;
                                                    return (
                                                        <button
                                                            key={cand.name + lane}
                                                            onClick={() => !disabled && handlePick(lane, cand.name)}
                                                            className={`text-left px-3 py-2 rounded-xl border
                                ${isSelected ? "border-emerald-400/30 bg-emerald-400/10" : "border-white/10 bg-white/5 hover:bg-white/10"}
                                ${disabled ? "opacity-50 cursor-not-allowed" : ""}
                              `}
                                                            title={disabled ? `Already picked for ${LANE_LABEL[takenElsewhere]}` : ""}
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                {iconByName(cand.name) && <img src={iconByName(cand.name)} alt={cand.name} className="w-6 h-6 rounded-md" />}
                                                                <div className="text-sm font-medium">
                                                                    {cand.name}
                                                                    {isSelected && <span className="text-[11px] ml-2 px-1 py-0.5 rounded bg-emerald-400/10 border border-emerald-400/30 text-emerald-200">selected</span>}
                                                                </div>
                                                                <div className="ml-auto text-xs text-slate-400">
                                                                    +{cand.coverageCount - 1} other challenges
                                                                </div>
                                                            </div>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Notes — specific to this challenge */}
                    <div className={`${CARD} p-4`}>
                        <div className="flex items-center gap-2 mb-2">
                            <Sparkles className="w-4 h-4 text-violet-300" />
                            <h3 className="text-sm font-bold text-slate-100">Notes — {selectedName}</h3>
                        </div>
                        <ul className="list-disc pl-5 space-y-1 text-sm text-slate-300 mb-3">
                            {notes.map((t,i)=><li key={i}>{t}</li>)}
                        </ul>

                        <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">
                            Eligible champions ({eligibleList.length}):
                        </div>
                        <div className="max-h-56 overflow-auto rounded-xl border border-white/10 bg-white/5 p-2">
                            <ul className="text-sm text-slate-300 space-y-1">
                                {eligibleList.map(e => (
                                    <li key={e.name}>
                                        <span className="font-medium">{e.name}</span>
                                        <span className="text-slate-400"> — {e.reason}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </section>
            </main>

            <footer className="max-w-6xl mx-auto px-3 sm:px-4 pb-8">
                <div className="text-xs text-slate-400/80">
          <span className="inline-flex items-center gap-1">
            <Sparkles className="w-3.5 h-3.5" /> Canonical roster only (your dataset). Data Dragon used for icons & role hints.
          </span>
                </div>
            </footer>
        </div>
    );
}
